<template>
  <div>
    <v-skeleton-loader
      v-if="initializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableRemote
      v-else-if="hasPermission('analyses.typesTypes.view')"
      :iconHeader="hasPermission('analyses.typesTypes.add') ? 'add' : null"
      :items="displayedAnalysisTypesTypes"
      isSearchable
      isCustomIcon
      :headers="tableHeaders"
      :itemActions="actions"
      :title="$t('analyses.analysisTypesTypes.pageName')"
      :centerColumns="['action']"
      :sortableColumns="sortableColumns"
      :notSortableColumns="['action']"
      :pagination="pagination"
      :loading="isFetching"
      hideDefaultFooter
      @iconAction="onClickAction"
      @click-icon-header="onClickAdd"
      @sendQueryParams="onQueryParams"
    >
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </VTableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import NameForm from '@/components/forms/NameForm';

export default {
    components: {
        VTableRemote,
    },
    data() {
        return {
            analysisTypesTypes: [],
            pagination: {},
            queryParams: {
                page: 1,
                perpage: 25,
                search: '',
            },
            isFetching: false,
            initializing: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'teamMainLang',
            'teamLangs',
        ]),
        tableHeaders() {
            return [
                'name',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        sortableColumns() {
            return this.tableHeaders.map(column => this.$options.filters.lowercase(column.value));
        },
        displayedAnalysisTypesTypes() {
            return this.analysisTypesTypes.map(analysisTypesType => ({
                id: analysisTypesType.id,
                name: analysisTypesType.name[this.userLang] && analysisTypesType.name[this.userLang].length
                    ? analysisTypesType.name[this.userLang] : analysisTypesType.name[this.teamMainLang],
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('analyses.typesTypes.edit')) {
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('analyses.typesTypes.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        this.initializing = true;
        await this.getAnalysisTypesTypes();
        this.initializing = false;
    },
    methods: {
        async getAnalysisTypesTypes(nextPage = 1) {
            if (this.isFetching) return;
            if (nextPage === 1 && this.queryParams.page === 1) {
                this.isFetching = true;
                this.queryParams.page = nextPage;
                const { data } = await this.$rq.getAnalysisTypesTypes(this.queryParams);
                this.analysisTypesTypes = [...data.analysisTypesTypes];
                this.pagination = data.pagination;
                this.isFetching = false;
                return;
            }
            if (nextPage <= this.queryParams.page) {
                return;
            }

            this.isFetching = true;
            this.queryParams.page = nextPage;
            if (this.queryParams.page === this.pagination.currentPage) return;
            const { data } = await this.$rq.getAnalysisTypesTypes(this.queryParams);
            this.analysisTypesTypes = [...this.analysisTypesTypes, ...data.analysisTypesTypes];
            this.pagination = data.pagination;
            this.isFetching = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('analyses.analysisTypesTypes.add'),
                component: Vue.component('NameForm', NameForm),
                showLangs: true,
                isForm: true,
                onConfirm: requestPayload => this.addAnalysisTypesType(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getAnalysisTypesType(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('analyses.analysisTypesTypes.update'),
                    component: Vue.component('NameForm', NameForm),
                    showLangs: true,
                    isForm: true,
                    payload: { name: cloneDeep(data), isEdition: true },
                    onConfirm: requestPayload => this.updateAnalysisTypesType(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('analyses.analysisTypesTypes.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('analyses.analysisTypesTypes.confirmRemove') },
                    onConfirm: () => this.removeAnalysisTypesType(item),
                    isForm: false,
                });
            }
        },
        async addAnalysisTypesType(requestPayload) {
            const payload = this.cleanPayload(requestPayload);
            await this.$rq.addAnalysisTypesType(payload);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.analysisTypesTypes.added', { name: requestPayload.name[this.userLang] }),
            });
            await this.getAnalysisTypesTypes();
        },
        async updateAnalysisTypesType(analysisTypesTypeId, requestPayload) {
            const payload = this.cleanPayload(requestPayload);
            const { data } = await this.$rq.updateAnalysisTypesType(analysisTypesTypeId, payload);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.analysisTypesTypes.updated', { name: data.name[this.userLang] }),
            });
            await this.getAnalysisTypesTypes();
        },
        async removeAnalysisTypesType(analysisTypesType) {
            await this.$rq.removeAnalysisTypesType(analysisTypesType.id);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.analysisTypesTypes.removed', { name: analysisTypesType.name[this.userLang] }),
            });
            await this.getAnalysisTypesTypes();
        },
        cleanPayload(payload) {
            const cleanedPayload = { name: {} };
            this.teamLangs.forEach(lang => {
                if (payload.name[lang] && payload.name[lang].length) {
                    cleanedPayload.name[lang] = payload.name[lang];
                }
            });
            return cleanedPayload;
        },
        onQueryParams(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
            };
            this.getAnalysisTypesTypes();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                await this.getAnalysisTypesTypes(this.queryParams.page + 1);
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
